import {useCallback, useEffect, useRef, useState} from 'react'
import {Reorder, AnimatePresence, motion} from 'framer-motion'
import {closeIcon, dragHandle, dragLines, trashIcon, locationIcon, plusLight} from '@img/index.js'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import PlaceAutocomplete from '@/customComponents/uiComponents/global/PlaceAutocomplete.jsx'
import {tripService} from '@/services/tripService'
import {locationAddress as locLabel, sameLoc} from '@/utils/functions'
import ModalFooter from '@/customComponents/uiComponents/modal/ModalFooter.jsx'
import useScrollOnError from '@/customComponents/uiComponents/modal/useScrollOnError.js'

export default function DestinationSection({destinations, committedDestinations, setDestinations, onDone, onClose, isLoading, error, mode = 'all'}) {

    // mode: 'origin' = origin only; 'dropoff' = stopovers + destination; 'all' = everything.
    // toAirport split: 'originStops' = origin + stopovers (first tab); 'destinationOnly' = destination only (second tab).
    const showOrigin = mode === 'origin' || mode === 'originStops' || mode === 'all'
    const showStopovers = mode === 'dropoff' || mode === 'originStops' || mode === 'all'
    const showDestination = mode === 'dropoff' || mode === 'destinationOnly' || mode === 'all'

    const {origin, destination, stopOvers} = destinations

    // The OTHER ends of this trip, for PlaceAutocomplete's `excludeNear`. An airport
    // already picked at one end (its pin, or one of its terminals) is kept out of every
    // other field's list, so the same airport can't be both the start and the destination.
    // `self` is the field being edited — it never excludes itself.
    const otherLocations = (self) =>
        [origin, destination, ...(stopOvers || [])]
            .filter((loc) => loc && loc !== self)
            .map((loc) => ({lat: loc.lat, lng: loc.lng}))
    const [errorFields, setErrorFields] = useState(new Set())
    const [routeChecking, setRouteChecking] = useState(false)
    const [routeError, setRouteError] = useState(null)
    useScrollOnError(error || routeError)
    const routeRequestIdRef = useRef(0)
    const routeFingerprintRef = useRef('')

    const committedStops = committedDestinations.stopOvers
    const hasChanges = !sameLoc(origin, committedDestinations.origin)
        || !sameLoc(destination, committedDestinations.destination)
        || stopOvers.length !== committedStops.length
        || stopOvers.some((s, i) => !sameLoc(s, committedStops[i]))

    useEffect(() => {
        if (!hasChanges) {
            setRouteChecking(false)
            setRouteError(null)
            routeFingerprintRef.current = ''
            return
        }
        const hasEmpty = !origin?.address || !destination?.address || stopOvers.some(s => !s.address)
        if (hasEmpty) {
            setRouteChecking(false)
            setRouteError(null)
            return
        }
        const originValid = origin?.lat && origin?.lng
        const destValid = destination?.lat && destination?.lng
        const stopsValid = stopOvers.every(s => s.lat && s.lng)
        if (!originValid || !destValid || !stopsValid) {
            setRouteChecking(false)
            setRouteError(null)
            return
        }
        const fingerprint = JSON.stringify({
            o: [origin.lat, origin.lng],
            d: [destination.lat, destination.lng],
            s: stopOvers.map(s => [s.lat, s.lng]),
        })
        if (fingerprint === routeFingerprintRef.current) {
            setRouteChecking(false)
            return
        }
        routeFingerprintRef.current = fingerprint

        const reqId = ++routeRequestIdRef.current
        setRouteChecking(true)
        setRouteError(null)
        const timer = setTimeout(async () => {
            // Routability check only — no pickup time, so this can't stamp the booking
            // flow's stored route window (the default would read ITS sessionStorage).
            const result = await tripService.calculateRoute(origin, destination, stopOvers, {scheduledPickupAt: null})
            if (reqId !== routeRequestIdRef.current) return
            if (!result.success) setRouteError(result.error || 'We could not find a route between these locations. Please check your pick-up and drop-off.')
            setRouteChecking(false)
        }, 450)
        return () => clearTimeout(timer)
    }, [hasChanges, origin, destination, stopOvers])

    const clearError = useCallback((field) => {
        setErrorFields(prev => { const n = new Set(prev); n.delete(field); return n })
    }, [])

    const updateOrigin = useCallback((data) => {
        clearError('origin')
        if (typeof data === 'string') {
            setDestinations(prev => ({...prev, origin: {...prev.origin, address: data}}))
        } else {
            setDestinations(prev => ({...prev, origin: {address: data.address, lat: data.lat, lng: data.lng, city: data.city || '', state: data.state || '', country: data.country || '', postal_code: data.postal_code || ''}}))
        }
    }, [setDestinations, clearError])

    const updateDestination = useCallback((data) => {
        clearError('destination')
        if (typeof data === 'string') {
            setDestinations(prev => ({...prev, destination: {...prev.destination, address: data}}))
        } else {
            setDestinations(prev => ({...prev, destination: {address: data.address, lat: data.lat, lng: data.lng, city: data.city || '', state: data.state || '', country: data.country || '', postal_code: data.postal_code || ''}}))
        }
    }, [setDestinations, clearError])

    const clearOrigin = useCallback(() => {
        const el = document.getElementById('edit-origin')
        if (el) el.value = ''
        setDestinations(prev => ({...prev, origin: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}}))
    }, [setDestinations])

    const clearDestination = useCallback(() => {
        const el = document.getElementById('edit-destination')
        if (el) el.value = ''
        setDestinations(prev => ({...prev, destination: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}}))
    }, [setDestinations])

    const addStopover = useCallback(() => {
        const newId = Date.now()
        setDestinations(prev => ({
            ...prev,
            stopOvers: [...prev.stopOvers, {id: newId, address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}]
        }))
        setTimeout(() => {
            const el = document.getElementById(`edit-stop-${newId}`)
            if (el) {
                el.scrollIntoView({behavior: 'smooth', block: 'center'})
                el.focus()
            }
        }, 150)
    }, [setDestinations])

    const removeStopover = useCallback((id) => {
        setDestinations(prev => ({...prev, stopOvers: prev.stopOvers.filter(s => s.id !== id)}))
    }, [setDestinations])

    const updateStopover = useCallback((id, data) => {
        clearError(id)
        if (typeof data === 'string') {
            setDestinations(prev => ({...prev, stopOvers: prev.stopOvers.map(s => s.id === id ? {...s, address: data} : s)}))
        } else {
            setDestinations(prev => ({...prev, stopOvers: prev.stopOvers.map(s => s.id === id ? {...s, address: data.address, lat: data.lat, lng: data.lng, city: data.city || '', state: data.state || '', country: data.country || '', postal_code: data.postal_code || ''} : s)}))
        }
    }, [setDestinations, clearError])

    const handleReorder = useCallback((newOrder) => {
        setDestinations(prev => ({...prev, stopOvers: newOrder}))
    }, [setDestinations])

    const handleDone = useCallback(() => {
        const errors = new Set()
        let firstErrorId = null
        if (!locLabel(origin)) { errors.add('origin'); firstErrorId = firstErrorId || 'edit-origin' }
        stopOvers.forEach(s => { if (!locLabel(s)) { errors.add(s.id); firstErrorId = firstErrorId || `edit-stop-${s.id}` } })
        if (!locLabel(destination)) { errors.add('destination'); firstErrorId = firstErrorId || 'edit-destination' }
        if (errors.size > 0) {
            setErrorFields(errors)
            if (firstErrorId) {
                const el = document.getElementById(firstErrorId)
                if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'})
            }
            return
        }
        setErrorFields(new Set())
        onDone?.()
    }, [origin, destination, stopOvers, onDone])

    return (
        <div>
            <div className="max-h-86 overflow-y-auto p-5 max-md:p-0 max-md:max-h-none max-md:overflow-visible">
                {/* Origin */}
                {showOrigin && (
                <div className={`bg-white rounded-(--radius-2xl) text-base mb-4 shadow-[0_4px_32px_#201e201f] border w-full py-5 px-9 ${errorFields.has('origin') ? 'border-(--text-red)' : 'border-[#201e20]'} max-md:px-4 max-md:py-2 max-md:rounded-3xl`}>
                    <label className="text-[#5b7fff] text-lg block mb-2">Start</label>
                    <div className="relative pl-10 max-md:pl-6">
                        <span className="absolute left-0 top-0.5"><img alt="location" src={locationIcon}/></span>
                        <div className="flex items-center">
                            <PlaceAutocomplete
                                key={locLabel(origin) ? 'origin-filled' : 'origin-empty'}
                                id="edit-origin"
                                value={locLabel(origin)}
                                placeholder="Enter start location"
                                onPlaceSelected={updateOrigin}
                                excludeNear={otherLocations(origin)}
                                onFocus={() => clearError('origin')}
                                readOnly={!!locLabel(origin)}
                            />
                            {locLabel(origin) && (
                                <button type="button" onClick={clearOrigin}
                                        className="shrink-0 w-8 h-8 bg-white rounded-(--radius-2xl) shadow-[0_4px_32px_#0000004f] flex items-center justify-center border-0 cursor-pointer p-0">
                                    <img src={closeIcon} alt="Clear" className="w-2.5"/>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                )}

                {/* Stopovers — framer-motion Reorder (always mounted so AnimatePresence works for first item) */}
                {showStopovers && (
                <Reorder.Group axis="y" values={stopOvers} onReorder={handleReorder} as="div" className={stopOvers.length > 0 ? 'pl-6 list-none p-0 m-0' : ''}>
                    <AnimatePresence>
                    {stopOvers.map((stop, index) => (
                        <Reorder.Item key={stop.id} value={stop} as="div" layout
                                      dragListener={stopOvers.length >= 2}
                                      className={`relative bg-white rounded-(--radius-2xl) text-base mb-4 shadow-[0_4px_32px_#201e201f] border w-full py-5 px-9 origin-top ${errorFields.has(stop.id) ? 'border-(--text-red)' : 'border-[#201e20]'} max-md:px-4 max-md:py-2 max-md:rounded-3xl`}
                                      initial={{opacity: 0, scale: 0.95}}
                                      animate={{opacity: 1, scale: 1}}
                                      exit={{opacity: 0, scale: 0.95}}
                                      transition={{duration: 0.2, ease: [0, 0, 0.2, 1]}}
                                      whileDrag={{scale: 1.03, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', zIndex: 50}}>
                            <label className="text-[#5b7fff] text-lg flex items-center gap-1.5 mb-2.5">
                                {stopOvers.length >= 2 && (
                                    <>
                                        {/* Mobile: hamburger handle (matches the booking flow); desktop keeps the arrows. */}
                                        <img className="w-6 opacity-60 cursor-grab active:cursor-grabbing max-md:hidden"
                                             alt="drag" src={dragHandle} draggable="false"/>
                                        <img className="w-5 cursor-grab active:cursor-grabbing md:hidden"
                                             alt="drag" src={dragLines} draggable="false"/>
                                    </>
                                )}
                                Stop {index + 1}
                            </label>
                            <div className="relative pl-10 max-md:pl-6">
                                <span className="absolute left-0 top-0.5"><img alt="location" src={locationIcon}/></span>
                                <div className="flex items-center">
                                    <PlaceAutocomplete
                                        key={locLabel(stop) ? `stop-${stop.id}-filled` : `stop-${stop.id}-empty`}
                                        id={`edit-stop-${stop.id}`}
                                        value={locLabel(stop)}
                                        placeholder="Enter stop location"
                                        onPlaceSelected={(data) => updateStopover(stop.id, data)}
                                        excludeNear={otherLocations(stop)}
                                        onFocus={() => clearError(stop.id)}
                                    />
                                    <button type="button" onClick={() => removeStopover(stop.id)}
                                            className="shrink-0 w-8 h-8 bg-white rounded-(--radius-2xl) shadow-[0_4px_32px_#0000004f] flex items-center justify-center border-0 cursor-pointer p-0">
                                        {/* Mobile: trash, like the booking flow's stopovers; desktop keeps ✕. */}
                                        <img src={closeIcon} alt="Remove" className="w-2.5 max-md:hidden"/>
                                        <img src={trashIcon} alt="Remove" className="w-4 md:hidden"/>
                                    </button>
                                </div>
                            </div>
                        </Reorder.Item>
                    ))}
                    </AnimatePresence>
                </Reorder.Group>
                )}

                {/* Destination */}
                {showDestination && (
                <div className={`bg-white rounded-(--radius-2xl) text-base mb-4 shadow-[0_4px_32px_#201e201f] border w-full py-5 px-9 ${errorFields.has('destination') ? 'border-(--text-red)' : 'border-[#201e20]'} max-md:px-4 max-md:py-2 max-md:rounded-3xl`}>
                    <label className="text-[#5b7fff] text-lg block mb-2">Destination</label>
                    <div className="relative pl-10 max-md:pl-6">
                        <span className="absolute left-0 top-0.5"><img alt="location" src={locationIcon}/></span>
                        <div className="flex items-center">
                            <PlaceAutocomplete
                                key={locLabel(destination) ? 'destination-filled' : 'destination-empty'}
                                id="edit-destination"
                                value={locLabel(destination)}
                                placeholder="Enter destination"
                                onPlaceSelected={updateDestination}
                                excludeNear={otherLocations(destination)}
                                onFocus={() => clearError('destination')}
                                readOnly={!!locLabel(destination)}
                            />
                            {locLabel(destination) && (
                                <button type="button" onClick={clearDestination}
                                        className="shrink-0 w-8 h-8 bg-white rounded-(--radius-2xl) shadow-[0_4px_32px_#0000004f] flex items-center justify-center border-0 cursor-pointer p-0">
                                    <img src={closeIcon} alt="Clear" className="w-2.5"/>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* Add stop button */}
            {showStopovers && (
            <button type="button" onClick={addStopover}
                    className="bg-transparent p-0 border-0 flex items-center ml-auto mt-7.5 mb-7.5 cursor-pointer text-base text-(--text-primary)">
                Want to pick someone else?
                <span className="inline-flex w-10 h-10 bg-[#b1fbe3] rounded-full items-center justify-center ml-2 shrink-0">
                    <img alt="add stopover" src={plusLight}/>
                </span>
            </button>
            )}

            {/* Error */}
            {(error || routeError) && <p className="text-red-500 text-sm mb-3 text-center">{error || routeError}</p>}

            {/* Done button */}
            <ModalFooter>
                <button type="button" onClick={handleDone}
                        disabled={!locLabel(origin) || !locLabel(destination) || stopOvers.some(s => !locLabel(s)) || !hasChanges || isLoading || routeChecking || !!routeError}
                        className="cursor-pointer w-full py-[15px] rounded-full bg-(--text-primary) text-white text-base flex items-center justify-center min-h-[55px] border-0 disabled:opacity-50">
                    {(isLoading || routeChecking) ? <Loader color="#fff"/> : 'Done'}
                </button>
            </ModalFooter>
        </div>
    )
}
